import { Injectable, inject } from '@angular/core';
import { GqlService, sharedAdminMutation, sharedAdminQueries } from '@tgx/shared/data-access/graphql';
import { AdminService } from '@tgx/shared/services';
import { MemberInfo, MembersData } from '@tgx/shared/interfaces';
import { MembersRole, PermissionsService } from '@tgx/shared/enums';

/**
 * Centralizes all permission-related API interactions for organization members.
 *
 * Two key API business rules govern every write operation in this service:
 *
 *   Rule 1 — Service permissions cannot be lower than the org-level role.
 *     The API rejects updateMemberServiceRole if the requested service role
 *     would end up below the member's current org role. To satisfy this constraint,
 *     when the minimum service role changes, UpdateRoleInOrganization is called
 *     first to bring the org role down to a safe baseline.
 *
 *   Rule 2 — UpdateRoleInOrganization resets ALL service roles.
 *     Calling UpdateRoleInOrganization sets every service to the specified role,
 *     not just the org role. After calling it, every service must be individually
 *     restored to its correct value via updateMemberServiceRole.
 */
@Injectable({
  providedIn: 'root',
})
export class UserPermissionsService {
  private gqlService = inject(GqlService);
  private adminService = inject(AdminService);

  private readonly roleMappings: { prop: keyof MemberInfo; service: PermissionsService }[] = [
    { prop: 'newRoleInHotelX', service: PermissionsService.HOTELX },
    { prop: 'newRoleInEntities', service: PermissionsService.ENTITIES },
    { prop: 'newRoleInBilling', service: PermissionsService.BILLING },
    { prop: 'newRoleInUser', service: PermissionsService.USER },
    { prop: 'newRoleInFastX', service: PermissionsService.FASTX },
  ];

  async getMemberRoleServiceInfo(email: string): Promise<MembersData> {
    const body = { where: { search: email, searchBy: 'CODE' } };

    return new Promise((resolve, reject) => {
      this.gqlService
        .queryGateway(sharedAdminQueries.getMemberProfileByCode, body)
        .then((response) => {
          const membersData: MembersData = response['admin']['member']['membersData'];
          if (membersData) {
            resolve(membersData);
          } else if (response['admin']['member']['adviseMessage']) {
            reject(response['admin']['member']['adviseMessage']);
          } else {
            reject(null);
          }
        })
        .catch((error) => reject(error));
    });
  }

  private async removeServiceRole(email: string, orgCode: string, service: PermissionsService): Promise<void> {
    await this.adminService.removeMemberServiceRole({ service }, { member: email, organization: orgCode });
  }

  private async updateMemberRoleInOrganization(email: string, orgCode: string, role: MembersRole): Promise<string> {
    return new Promise((resolve, reject) => {
      this.gqlService
        .mutationGateway(sharedAdminMutation.updateMemberDataInOrganization, {
          where: { member: email, organization: orgCode },
          data: { role },
        })
        .then((response: any) => {
          const error = response.admin.updateMemberDataInOrganization.adviseMessage;
          const code: string = response.admin.updateMemberDataInOrganization.code;
          if (error?.length > 0) {
            reject(error);
          } else {
            resolve(code);
          }
        })
        .catch((error) => reject(error));
    });
  }

  /**
   * Applies service roles for a member in bulk, respecting both API business rules.
   *
   * Returns the list of services that failed to update (empty if all succeeded) and
   * whether any service role actually changed. The caller is responsible for presenting
   * any error feedback to the user.
   */
  async applyServiceRoles(
    email: string,
    orgCode: string,
    currentData: MemberInfo,
    profileFormBackup: MemberInfo,
    isUpdate: boolean,
  ): Promise<{ failedServices: PermissionsService[]; rolesChanged: boolean }> {
    // Determine which service roles have actually changed (or all of them in create mode).
    const tasks = this.roleMappings.filter((m) => {
      const newVal = currentData[m.prop];
      const oldVal = profileFormBackup[m.prop];

      if (isUpdate) {
        return newVal !== oldVal;
      }

      // In create mode we also need to persist VIEWER roles explicitly.
      return newVal !== undefined && newVal !== null;
    });

    if (tasks.length === 0) return { failedServices: [], rolesChanged: false };

    // Collect ALL services with a defined role.
    // This list is used when the org role changes, because that backend call resets
    // every service role and then all services must be restored explicitly.
    const billingTask = this.roleMappings.find((m) => m.service === PermissionsService.BILLING);
    const isBillingDisabled = billingTask ? currentData[billingTask.prop] == null : false;
    const previousBillingRole = billingTask ? profileFormBackup[billingTask.prop] : null;
    const billingChangedToDisabled = billingTask ? previousBillingRole != null && isBillingDisabled : false;
    const allServiceTasks = this.roleMappings.filter((m) => {
      const val = currentData[m.prop];
      return val !== undefined && val !== null;
    });
    let roleChanged = false;

    // Compute and update org role only when there are active service roles.
    if (allServiceTasks.length > 0) {
      const allRoles = allServiceTasks.map((t) => currentData[t.prop] as MembersRole);
      const minRole = this.getMinimumRole(allRoles);

      // In some edit flows, org role can be missing in backup payloads; in that case,
      // infer the previous org role from previous service roles to avoid redundant calls.
      const backupServiceRoles = this.roleMappings
        .map((m) => profileFormBackup[m.prop] as MembersRole)
        .filter((role) => role !== undefined && role !== null);
      const inferredBackupOrgRole = backupServiceRoles.length > 0 ? this.getMinimumRole(backupServiceRoles) : null;
      const currentOrgRole = isUpdate
        ? (profileFormBackup.role ?? inferredBackupOrgRole)
        : (currentData.role ?? inferredBackupOrgRole);
      roleChanged = currentOrgRole !== minRole;
    }

    if (roleChanged) {
      try {
        const allRoles = allServiceTasks.map((t) => currentData[t.prop] as MembersRole);
        const minRole = this.getMinimumRole(allRoles);
        await this.updateMemberRoleInOrganization(email, orgCode, minRole);
      } catch {
        // Abort to avoid partial/inconsistent permission updates.
        throw new Error('Failed to update organization role before applying service permissions.');
      }
    }

    // If org role changed, backend resets every service role, so restore all services.
    // If org role did not change, update only services the user actually modified.
    const servicesToUpdate = (roleChanged ? allServiceTasks : tasks).filter(
      (task) => !(task.service === PermissionsService.BILLING && isBillingDisabled),
    );

    const results = await Promise.allSettled(
      servicesToUpdate.map((task) =>
        this.adminService.updateMemberServiceRole(email, orgCode, currentData[task.prop] as MembersRole, task.service),
      ),
    );
    const failedServices = servicesToUpdate.filter((_, i) => results[i].status === 'rejected').map((t) => t.service);

    // Billing remove must only run in these cases:
    // - create flow with Billing disabled.
    // - update flow where Billing explicitly changed from enabled -> disabled.
    // - update flow where org role changed AND Billing previously had a role:
    //   the backend reset restores all service roles that existed, so we must
    //   remove Billing again. If Billing had no role before, the backend did not
    //   assign one either, so no remove is needed.
    const shouldRemoveBilling =
      isBillingDisabled && (!isUpdate || billingChangedToDisabled || (roleChanged && previousBillingRole != null));

    if (shouldRemoveBilling) {
      try {
        await this.removeServiceRole(email, orgCode, PermissionsService.BILLING);
      } catch {
        failedServices.push(PermissionsService.BILLING);
      }
    }

    return { failedServices, rolesChanged: true };
  }

  // Returns the lowest role in the hierarchy among the provided roles.
  // Used to compute the safe org-level baseline before updating service roles (Rule 1).
  private getMinimumRole(roles: MembersRole[]): MembersRole {
    const hierarchy: MembersRole[] = [
      MembersRole.GUEST,
      MembersRole.VIEWER,
      MembersRole.EDITOR,
      MembersRole.ADMIN,
      MembersRole.OWNER,
    ];
    return roles.reduce((min, role) => {
      const minIndex = hierarchy.indexOf(min);
      const roleIndex = hierarchy.indexOf(role);
      return roleIndex < minIndex ? role : min;
    }, roles[0]);
  }
}
